/**
 * Per-broker observability — order latency/throughput/errors + event-loop lag.
 * Backed by GET /admin/observability (api/routes/observability.py), fed from the
 * always-on in-process collector. Broker-agnostic: one row per broker_id.
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get } from './client';

const latencySchema = z.object({
  p50: z.number().nullable(),
  p95: z.number().nullable(),
  p99: z.number().nullable(),
  max: z.number().nullable().optional(),
  samples: z.number(),
});

export const brokerObservabilitySchema = z.object({
  broker: z.string(),
  orders: z.number(),
  errors: z.number(),
  error_rate: z.number(),
  errors_by_kind: z.record(z.number()),
  last_error: z.string().nullable(),
  last_order_at: z.number().nullable(),
  order_latency_ms: latencySchema,
  signal_to_order_ms: latencySchema,
  // Governor (Phase 3)
  circuit: z.enum(['closed', 'half_open', 'open']).default('closed'),
  consecutive_failures: z.number().default(0),
  rate_per_sec: z.number().nullable().default(null),
});

export const observabilitySnapshotSchema = z.object({
  event_loop_lag_ms: z.number(),
  brokers: z.array(brokerObservabilitySchema),
});

export type BrokerObservability = z.infer<typeof brokerObservabilitySchema>;
export type ObservabilitySnapshot = z.infer<typeof observabilitySnapshotSchema>;

export const getObservability = (
  signal?: AbortSignal,
): Promise<ApiResult<ObservabilitySnapshot>> =>
  get('/admin/observability', observabilitySnapshotSchema, { signal });
